import React, { useState, ReactNode, useEffect } from "react";
import eq from "fast-deep-equal";
import {
	DataObserveKey,
	VisualComponent,
	FocusableComponent,
	Content,
	Link,
	SrOnly,
	useUniqueId,
} from "@siteimprove/fancylib";
import {
	BaseTable,
	BaseTableCellPosition,
	BaseTableColumnProps,
	ColumnConfig,
	HeaderConfig,
	SortableHeaderConfig,
	BasicHeaderConfig,
	ColumnConfigOptions,
	ColumnsArray,
} from "../base-table/base-table";
import {
	MultiColumnHeader,
	ColumnHeader,
	BaseColumnHeader,
	SortField,
	isSortable,
} from "../column-header/column-header";
import { Pagination, PaginationProps } from "../../navigation/pagination/pagination";
import { isNotNullOrFalse } from "../../../utils/shorthands";
import { useLabTranslations } from "../../../translations/translations";

export type TableProps<TDto> = {
	/** Configurations for the columns displayed in the table */
	columns: ColumnsArray<TDto>;
	/** Items displayed in the table */
	items: TDto[];
	/** Property and direction by which the table is sorted */
	sort: SortField<TDto> | null;
	/** Callback for updating sorting */
	setSort: (property: Extract<keyof TDto, string>, direction: "asc" | "desc") => void;
	/** Is the table in a loading state? */
	loading: boolean;
	/** Optional text to be displayed when the table is loading */
	loadingText?: string;
	/** Optional footer to be displayed below the table */
	footer?: React.ReactNode;
	/** Pagination */
	pagination?: PaginationProps;
	/** Sets the data inside the table to be top-aligned */
	topAligned?: boolean;
	/** Compare function for highlight row */
	highlightRow?: (dto: TDto) => boolean;
	/** Function to generate a key for rows */
	rowKey?: (dto: TDto) => string | number;
	/** Caption for the table */
	caption?: string;
	/** Content to be shown when there's no data. An Empty State component is shown by default, but it can be overridden by another type with custom text. */
	noDataState?: React.ReactNode;
	/** Does this table not have a key column? Be sure this is the case otherwise the table is less accessible */
	withoutKeyColumn?: boolean;
} & DataObserveKey &
	VisualComponent;

export * from "./table-exporter";
export * from "./table-csv-exporter";

type ExpandedCell = [BaseTableCellPosition, number | null];

function isBasicHeaderConfig<TDto>(
	props: HeaderConfig<TDto> | SortableHeaderConfig<TDto>[] | BasicHeaderConfig
): props is BasicHeaderConfig {
	return "contentNode" in props;
}

export function isColumnConfig<TDto>(x: ColumnsArray<TDto>[number]): x is ColumnConfig<TDto> {
	return x !== null && x !== false;
}

export function Table<TDto>(props: TableProps<TDto>): JSX.Element {
	const {
		loading: isLoading,
		sort,
		setSort,
		footer,
		pagination,
		topAligned,
		highlightRow,
		columns,
		items,
		withoutKeyColumn,
		rowKey,
		loadingText,
		noDataState,
		caption,
		className,
		style,
	} = props;
	const pageNumber = pagination ? pagination.page : null;
	const [expandedCell, setExpandedCell] = useState<ExpandedCell | null>(null);
	const weightedColumns = columns
		.map((c, i) => [c, i] as const)
		.sort(
			([aCol, aIdx], [bCol, bIdx]) =>
				((aCol && aCol.options?.weight) || aIdx) - ((bCol && bCol.options?.weight) || bIdx)
		)
		.map(([col]) => col);
	const validColumns = weightedColumns.filter(isNotNullOrFalse);
	const columnsConfigs = validColumns.map((c) => {
		const opts: ColumnConfigOptions<TDto> = c.options || {}; // Defensive options
		const headerCell = isBasicHeaderConfig(c.header)
			? basicHeader(c.header)
			: header(c.header, sort, setSort);

		const r: BaseTableColumnProps<TDto> = {
			align: opts.align,
			width: opts.width,
			cellPadding: opts.cellPadding,
			colSpan: opts.colSpan,
			render: c.render,
			sorted:
				(c.header &&
					sort &&
					(Array.isArray(c.header)
						? c.header.some((h) => isSortable(h) && h.property === sort.property) && sort.direction
						: !isBasicHeaderConfig(c.header) &&
						  isSortable(c.header) &&
						  c.header.property === sort.property &&
						  sort.direction)) ||
				undefined,
			header: headerCell,
			isExpandable: c.expandOptions ? c.expandOptions.canCellExpand : undefined,
			isKeyColumn: c.options ? c.options.isKeyColumn : undefined,
			overrideOptionsFor: c.overrideOptionsFor,
			shouldRenderCell: c.shouldRenderCell,
			className: c.options?.className,
			hideInStaticMode: opts.hideInStaticMode,
			summary: c.summary,
			dataObserveKeys: opts.dataObserveKeys,
		};
		return r;
	});

	// Close expanded cells if table contents change to avoid showing incorrect data
	useEffect(() => {
		setExpandedCell(null);
	}, [items]);

	const skipTableAnchorId = useUniqueId("skipTable");
	const i18n = useLabTranslations();

	return (
		<>
			<SrOnly>
				<Link href={`#${skipTableAnchorId}`}>{i18n.skipTable}</Link>
			</SrOnly>
			<BaseTable
				rowKey={rowKey}
				data-component="table"
				data-observe-key={props["data-observe-key"]}
				columns={columnsConfigs}
				items={items}
				onExpandClick={(e, cellPos) => {
					const cell: ExpandedCell = [cellPos, pageNumber];
					if (eq(expandedCell, cell)) {
						setExpandedCell(null);
					} else {
						setExpandedCell(cell);
					}
				}}
				expandedCellPosition={resolveExpandedCellPosistion(expandedCell, pageNumber)}
				expandedContentRenderer={(item, cellPosition) => {
					const opts = validColumns[cellPosition.columnNum].expandOptions;
					const renderer = opts && opts.cellExpandRenderer;
					return renderer ? renderer(item) : null;
				}}
				footer={footer}
				topAligned={topAligned}
				highlightRow={highlightRow}
				caption={caption}
				withoutKeyColumn={withoutKeyColumn}
				className={className}
				style={style}
				loading={isLoading}
				loadingText={loadingText}
				noDataState={noDataState}
			/>
			{pagination && items.length > 0 && (
				<Content>
					<Pagination {...pagination} />
				</Content>
			)}
			<div id={skipTableAnchorId} />
		</>
	);
}

function header<TDto>(
	header: HeaderConfig<TDto> | SortableHeaderConfig<TDto>[] | undefined,
	sort: SortField<TDto> | null,
	onSort: (property: Extract<keyof TDto, string>, direction: "asc" | "desc") => void
): ReactNode {
	if (!header) {
		return null;
	}
	const headerSort = { sort, onSort };
	if (Array.isArray(header)) {
		return <MultiColumnHeader {...headerSort} columns={header} />;
	}
	const { property, content, tooltip, defaultSortDirection, notSortable } = header;
	if (!notSortable && content && property) {
		return (
			<ColumnHeader
				{...headerSort}
				content={content}
				tooltip={tooltip}
				property={property}
				defaultSortDirection={defaultSortDirection}
				data-observe-key={header["data-observe-key"]}
			/>
		);
	}

	if (content) {
		return (
			<ColumnHeader
				content={content}
				tooltip={tooltip}
				data-observe-key={header["data-observe-key"]}
			/>
		);
	}
}

function basicHeader(
	header: BasicHeaderConfig & DataObserveKey & VisualComponent & FocusableComponent
): ReactNode {
	const { contentNode, ...rest } = header;
	return <BaseColumnHeader content={contentNode} {...rest} />;
}

function resolveExpandedCellPosistion(cell: ExpandedCell | null, pageNumber: number | null) {
	if (cell === null) {
		return undefined;
	}
	const [pos, pn] = cell;
	return pageNumber === pn ? pos : undefined;
}

/**
 * Takes a direction and inverts it
 * @param direction - current direction to be inverted
 * @returns inverted direction ("asc" -> "desc"|"desc" -> "asc")
 */
export function invertDirection(direction: "asc" | "desc"): "asc" | "desc" {
	return direction === "asc" ? "desc" : "asc";
}
